export default (element: HTMLElement) => {
  // Get configuration from data attributes
  const loginUntilStr = element.dataset.loginUntil;
  if (!loginUntilStr) {
    console.error('Missing data-login-until attribute on loginTimer component');
    return;
  }

  // Get DOM elements
  const timerStatusElement = element.querySelector<HTMLElement>(
    '[data-timer-status]',
  );
  const prolongContainer = element.querySelector<HTMLElement>(
    '[data-prolong-container]',
  );
  const prolongButton = element.querySelector<HTMLButtonElement>(
    '[data-prolong-button]',
  );
  const pillButton = element.querySelector<HTMLElement>(
    '.expandablePill__button',
  );

  if (
    !timerStatusElement ||
    !prolongContainer ||
    !prolongButton ||
    !pillButton
  ) {
    console.error('Missing required elements in loginTimer component');
    return;
  }

  // TypeScript non-null assertions (already validated above)
  const timerStatus = timerStatusElement;
  const prolongCont = prolongContainer;
  const prolongBtn = prolongButton;
  const pill = pillButton;

  // State
  let loginUntil: Date = new Date(loginUntilStr);
  let updateInterval: number | null = null;
  let refreshTimeout: number | null = null;
  let logoutPopoverShown = false;

  // Update timer display
  function updateDisplay() {
    const now = new Date();
    const remainingMs = loginUntil.getTime() - now.getTime();
    const remainingMinutes = Math.max(0, Math.floor(remainingMs / 60_000));

    // Update text
    const text = `Zbývá ${remainingMinutes} ${getMinutesWord(remainingMinutes)} do automatického odhlášení`;
    timerStatus.textContent = text;

    // Update progress bar (30 minutes = 100%)
    const progressPercent = Math.min(100, (remainingMinutes / 30) * 100);
    pill.style.setProperty('--expandablePill-progress', `${progressPercent}%`);

    // Show/hide prolong button
    if (remainingMinutes < 15 && remainingMs > 0) {
      prolongCont.style.display = '';
    } else {
      prolongCont.style.display = 'none';
    }

    // Check if session expired
    if (remainingMs <= 0 && !logoutPopoverShown) {
      handleSessionExpired();
    }
  }

  // Get Czech word for minutes (1 minuta, 2-4 minuty, 5+ minut)
  function getMinutesWord(count: number): string {
    if (count === 1) return 'minuta';
    if (count >= 2 && count <= 4) return 'minuty';
    return 'minut';
  }

  // Handle session expiration
  async function handleSessionExpired() {
    logoutPopoverShown = true;

    // Verify with server
    try {
      const response = await fetch('/api/login-until');
      if (response.status === 401) {
        showLogoutNotification();
      } else if (response.ok) {
        const data = await response.json();
        loginUntil = new Date(data.login_until);
        logoutPopoverShown = false;
        updateDisplay();
      }
    } catch (error) {
      console.error('Error checking login status:', error);
      showLogoutNotification();
    }
  }

  // Show logout notification popover
  function showLogoutNotification() {
    // Stop all timers
    if (updateInterval) clearInterval(updateInterval);
    if (refreshTimeout) clearTimeout(refreshTimeout);

    // Create popover element
    const popover = document.createElement('div');
    popover.className = 'logoutNotification';
    popover.textContent = 'Byl jsi automaticky odhlášen pro neaktivitu.';
    document.body.appendChild(popover);
  }

  // Refresh login time from server
  async function refreshLoginTime() {
    try {
      const response = await fetch('/api/login-until');

      if (response.status === 401) {
        handleSessionExpired();
        return;
      }

      if (!response.ok) {
        throw new Error('Failed to refresh login time');
      }

      const data = await response.json();
      loginUntil = new Date(data.login_until);
      updateDisplay();
    } catch (error) {
      console.error('Error refreshing login time:', error);
      // Continue with last known time
    }

    // Schedule next refresh
    scheduleRefresh();
  }

  // Schedule next refresh
  function scheduleRefresh() {
    if (refreshTimeout) clearTimeout(refreshTimeout);
    refreshTimeout = window.setTimeout(refreshLoginTime, 60_000); // Refresh every 1 minute
  }

  // Prolong session
  async function prolongSession() {
    try {
      prolongBtn.disabled = true;
      prolongBtn.textContent = 'Prodlužuji...';

      const response = await fetch('/api/login-prolongation', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to prolong session');
      }

      const data = await response.json();
      loginUntil = new Date(data.login_until);
      updateDisplay();

      prolongBtn.disabled = false;
      prolongBtn.textContent = 'Prodloužit přihlášení';
    } catch (error) {
      console.error('Error prolonging session:', error);
      prolongBtn.disabled = false;
      prolongBtn.textContent = 'Prodloužit přihlášení';
    }
  }

  // Event listeners
  prolongBtn.addEventListener('click', prolongSession);

  // Start updates
  updateDisplay();
  updateInterval = window.setInterval(updateDisplay, 10_000); // Update every 10 seconds
  scheduleRefresh(); // Start periodic refresh

  // Cleanup on component removal
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.removedNodes.forEach((node) => {
        if (node === element) {
          if (updateInterval) clearInterval(updateInterval);
          if (refreshTimeout) clearTimeout(refreshTimeout);
          observer.disconnect();
        }
      });
    });
  });

  if (element.parentElement) {
    observer.observe(element.parentElement, { childList: true });
  }
};
